// WHAT: Welcome screen component for MiracleFlow sessions with step overview and start button.
// WHY:  Provides introduction to session workflow, displays session steps, and allows users to start a new session.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @/components/ui, @tracq/frontend-shared/utils, lucide-react
// RISKS:
//   - NOTE: Session configuration (steps, icons, descriptions) is hardcoded; changes require code updates.
//   - WARN: Start button is disabled when hasActiveSession is true; ensure session state is properly managed.
//   - NOTE: Step count differs between morning (6) and evening (4); ensure UI handles both correctly.

import {
  Sun,
  Moon,
  Play,
  Sparkles,
  Target,
  Clock,
  Heart,
  Brain,
  Zap,
  BookOpen,
  PenTool,
  Dumbbell,
  Eye,
  BrainCircuit,
} from "lucide-react";

import { Card, Button, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

interface ModernSessionWelcomeProperties {
  readonly hasActiveSession?: boolean;
  readonly isLoading: boolean;
  readonly onStartSession: () => void;
  readonly sessionType: "Evening" | "Morning";
}

const sessionConfig = {
  Evening: {
    description:
      "End your day with reflection, affirmations, visualization, and meditation to prepare for tomorrow.",
    gradient: "from-tracq-deep-orange/20 to-tracq-light-orange/20",
    icon: Moon,
    iconGradient: "from-tracq-deep-orange to-tracq-light-orange",
    steps: [
      {
        description: "Review your day",
        emoji: "🤔",
        icon: Brain,
        name: "Reflection",
      },
      {
        description: "Evening gratitude",
        emoji: "💭",
        icon: Heart,
        name: "Affirmations",
      },
      {
        description: "Tomorrow's goals",
        emoji: "🎯",
        icon: Eye,
        name: "Visualization",
      },
      {
        description: "Peaceful rest",
        emoji: "🧘",
        icon: BrainCircuit,
        name: "Meditation",
      },
    ],
    subtitle: "End your day with gratitude and preparation",
    title: "Ready for Evening Reflection?",
  },
  Morning: {
    description:
      "Start your morning session with meditation, affirmations, visualization, exercise, reading, and scribing.",
    gradient: "from-tracq-deep-blue/20 to-tracq-bright-blue/20",
    icon: Sun,
    iconGradient: "from-tracq-deep-blue to-tracq-bright-blue",
    steps: [
      {
        description: "Center your mind",
        emoji: "🧘",
        icon: Brain,
        name: "Meditation",
      },
      {
        description: "Positive mindset",
        emoji: "💭",
        icon: Heart,
        name: "Affirmations",
      },
      {
        description: "See your goals",
        emoji: "🎯",
        icon: Eye,
        name: "Visualization",
      },
      {
        description: "Energize your body",
        emoji: "💪",
        icon: Dumbbell,
        name: "Exercise",
      },
      {
        description: "Expand your mind",
        emoji: "📚",
        icon: BookOpen,
        name: "Reading",
      },
      {
        description: "Capture insights",
        emoji: "✍️",
        icon: PenTool,
        name: "Scribing",
      },
    ],
    subtitle: "Begin your day with intention and purpose",
    title: "Ready to Start Your Morning?",
  },
};

/**
 * WHAT:
 *   Welcome screen component for MiracleFlow sessions.
 *
 * WHY:
 *   Provides users with introduction to session workflow, displays session steps with icons
 *   and descriptions, and allows users to start a new session. Differentiates between
 *   morning and evening sessions with type-specific configurations.
 *
 * PROPS:
 *   - sessionType: 'Morning' | 'Evening' - Type of session to display
 *   - onStartSession: () => void - Callback to start session
 *   - isLoading: boolean - Loading state for start operation
 *   - hasActiveSession?: boolean - Flag indicating if a session is already active (default: false)
 *
 * BEHAVIOR:
 *   - Displays hero section with session type icon and title
 *   - Shows session description and subtitle
 *   - Lists session steps with icons, names, emojis, and descriptions
 *   - Provides start button (disabled if session is active or loading)
 *   - Applies type-specific gradient colors and styling
 *   - Morning session shows 6 steps, evening session shows 4 steps
 *
 * RISKS:
 *   - NOTE: Session configuration (steps, icons, descriptions) is hardcoded; changes require code updates.
 *   - WARN: Start button is disabled when hasActiveSession is true; ensure session state is properly managed.
 *   - NOTE: Step count differs between morning (6) and evening (4); ensure UI handles both correctly.
 *
 * @param root0
 *
 * @param root0.sessionType
 *
 * @param root0.onStartSession
 *
 * @param root0.isLoading
 *
 * @param root0.hasActiveSession
 *
 * @returns JSX.Element
 *
 */
export function ModernSessionWelcome({
  hasActiveSession = false,
  isLoading,
  onStartSession,
  sessionType,
}: ModernSessionWelcomeProperties) {
  const config = sessionConfig[sessionType];
  const Icon = config.icon;

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <Card
        className={`bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-[#334155]`}
        padding="lg"
        variant="elevated"
      >
        <Stack align="center" direction="col" gap={8}>
          {/* Main Icon & Title */}
          <Stack align="center" direction="col" gap={4}>
            <div
              className={`flex items-center justify-center w-24 h-24 rounded-tracq bg-gradient-to-br ${config.iconGradient} shadow-tracq-card`}
            >
              <Icon className="w-12 h-12 text-white" />
            </div>
            <Stack align="center" direction="col" gap={2}>
              <Heading className="text-[#F1F5F9] font-bold text-center" level="h2" size="2xl">
                {config.title}
              </Heading>
              <p className="text-[#94A3B8] text-lg text-center max-w-2xl">{config.description}</p>
            </Stack>
          </Stack>

          {/* Start Button */}
          <Button
            className={cn(
              "px-12 py-4 text-lg font-semibold shadow-tracq-card transition-all duration-300",
              sessionType === "Morning"
                ? "bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90"
                : "bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange hover:from-tracq-deep-orange/90 hover:to-tracq-light-orange/90",
            )}
            disabled={isLoading || hasActiveSession}
            onClick={onStartSession}
            size="lg"
            variant="primary"
          >
            <Play className="w-6 h-6 mr-3" />
            {isLoading ? "Starting..." : `Start ${sessionType} Session`}
          </Button>
        </Stack>
      </Card>

      {/* Session Steps Grid */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-bright-blue to-tracq-deep-blue shadow-tracq-card">
              <Target className="w-5 h-5 text-white" />
            </div>
            <Heading className="text-[#F1F5F9]" level="h3" size="lg">
              Session Steps
            </Heading>
          </Stack>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {config.steps.map((step) => (
              <div
                className="group p-4 sm:p-5 lg:p-6 bg-[#0F172A] rounded-tracq border border-[#334155] hover:border-tracq-bright-blue/30 transition-all duration-300 hover:bg-[#1E293B]"
                key={step.name}
              >
                <Stack align="center" className="w-full" direction="col" gap={2}>
                  <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#334155] group-hover:bg-tracq-bright-blue/20 transition-colors duration-300 flex-shrink-0">
                    <span className="text-xl sm:text-2xl">{step.emoji}</span>
                  </div>
                  <Stack align="center" className="w-full min-w-0" direction="col" gap={1}>
                    {/* Step Name - Responsive sizing */}
                    <h4
                      className={cn(
                        "text-[#F1F5F9] font-semibold text-center w-full",
                        "text-sm sm:text-base lg:text-lg",
                        "truncate sm:whitespace-normal",
                      )}
                    >
                      {step.name}
                    </h4>
                    {/* Step Description - Hidden on xs, shown on sm+, full on lg+ */}
                    <p
                      className={cn(
                        "text-[#94A3B8] text-center w-full",
                        "hidden sm:block text-xs sm:text-sm",
                      )}
                    >
                      {/* Short description on sm/md, full on lg+ */}
                      <span className="sm:inline lg:hidden">
                        {step.description.length > 15
                          ? step.description.slice(0, 15) + "..."
                          : step.description}
                      </span>
                      <span className="hidden lg:inline">{step.description}</span>
                    </p>
                  </Stack>
                </Stack>
              </div>
            ))}
          </div>
        </Stack>
      </Card>

      {/* Benefits Section */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <Heading className="text-[#F1F5F9]" level="h3" size="lg">
              Session Benefits
            </Heading>
          </Stack>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-tracq-bright-blue/20 mx-auto mb-3">
                <Clock className="w-6 h-6 text-tracq-bright-blue" />
              </div>
              <h4 className="text-[#F1F5F9] font-semibold mb-2">Daily Routine</h4>
              <p className="text-[#94A3B8] text-sm">
                Build consistent habits for long-term success
              </p>
            </div>

            <div className="text-center p-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-green-500/20 mx-auto mb-3">
                <Brain className="w-6 h-6 text-green-500" />
              </div>
              <h4 className="text-[#F1F5F9] font-semibold mb-2">Mental Clarity</h4>
              <p className="text-[#94A3B8] text-sm">Enhance focus and decision-making abilities</p>
            </div>

            <div className="text-center p-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-tracq-light-orange/20 mx-auto mb-3">
                <Zap className="w-6 h-6 text-tracq-light-orange" />
              </div>
              <h4 className="text-[#F1F5F9] font-semibold mb-2">Personal Growth</h4>
              <p className="text-[#94A3B8] text-sm">Continuous improvement and self-development</p>
            </div>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
